import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, PlayCircle, MapPin } from "lucide-react";
import { useLang, scrollToId } from "@/i18n";
import PanoPlayer from "./PanoPlayer";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-2 -mb-2 pr-6 -mr-6">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const { lang, t } = useLang();
  const [playSignal, setPlaySignal] = useState(0);
  const tiltRef = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [5, -5]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-6, 6]), { stiffness: 120, damping: 18 });

  const onTiltMove = (e) => {
    const rect = tiltRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };

  return (
    <section className="relative overflow-hidden pt-32 md:pt-40 pb-16 md:pb-24">
      <div className="pointer-events-none absolute -top-40 -left-40 h-[480px] w-[480px] rounded-full bg-cyan-500/15 blur-[130px]" />
      <div className="pointer-events-none absolute top-1/3 -right-48 h-[520px] w-[520px] rounded-full bg-violet-600/15 blur-[140px]" />
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-[300px] w-[300px] rounded-full bg-blue-600/10 blur-[110px]" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2 items-center gap-12 lg:gap-16 px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col items-start">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mb-7 flex items-center gap-3 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-5 min-h-10"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 animate-pulse" />
            <span className="font-mono-accent text-[11px] sm:text-xs uppercase tracking-[0.22em] text-cyan-300">
              {t.hero.badge}
            </span>
          </motion.div>

          <h1
            key={lang}
            data-testid="hero-headline"
            className="font-display text-[2rem] sm:text-5xl lg:text-[3.4rem] font-bold tracking-tight leading-[1.14]"
          >
            <MaskedLine delay={0.15}>{t.hero.line1}</MaskedLine>
            <MaskedLine delay={0.27}>{t.hero.line2}</MaskedLine>
            <MaskedLine delay={0.39}>
              <span className="text-vortex-gradient">{t.hero.line3a}</span>
            </MaskedLine>
            <MaskedLine delay={0.51}>{t.hero.line3b}</MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => scrollToId("contact")}
              className="vortex-btn group inline-flex items-center gap-3 min-h-14 px-8 rounded-full text-base font-bold text-white"
            >
              {t.hero.cta}
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1.5" />
            </button>
            <button
              data-testid="hero-demo-cta"
              onClick={() => setPlaySignal((s) => s + 1)}
              className="inline-flex items-center gap-3 min-h-14 px-7 rounded-full text-base font-semibold text-slate-200 border border-white/15 hover:border-cyan-400/50 hover:text-cyan-300 transition-colors duration-300"
            >
              <PlayCircle className="w-5 h-5" />
              {t.hero.ctaSecondary}
            </button>
          </motion.div>

          <motion.dl
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-5"
          >
            {t.hero.stats.map((s) => (
              <div key={s.v}>
                <dt className="sr-only">{s.l}</dt>
                <dd className="font-mono-accent text-xl sm:text-2xl font-semibold text-vortex-gradient">{s.v}</dd>
                <dd className="mt-1 text-xs uppercase tracking-[0.18em] text-slate-500">{s.l}</dd>
              </div>
            ))}
          </motion.dl>

          <motion.p
            data-testid="hero-service-area"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-8 flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] px-5 min-h-12 text-sm text-slate-300"
          >
            <MapPin className="w-4 h-4 text-cyan-400 shrink-0" />
            {t.hero.area}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
          style={{ perspective: 1200 }}
        >
          <motion.div
            ref={tiltRef}
            onMouseMove={onTiltMove}
            onMouseLeave={() => {
              mx.set(0.5);
              my.set(0.5);
            }}
            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
          >
            <PanoPlayer playSignal={playSignal} />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
